// Auth utility for managing tokens and authentication

import { API_BASE_URL } from '../config/api';

// Cookie helpers
export const setCookie = (name: string, value: string, days: number = 30) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Token TTL in milliseconds (1 hour)
const TOKEN_TTL_MS = 60 * 60 * 1000;

// Token management
export const saveTokens = (authToken: string, refreshToken: string) => {
  localStorage.setItem('authToken', authToken);
  localStorage.setItem('authTokenTimestamp', Date.now().toString());
  setCookie('refreshToken', refreshToken, 30); // Save refresh token in cookie for 30 days
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const getAuthTokenTimestamp = (): number | null => {
  const timestamp = localStorage.getItem('authTokenTimestamp');
  return timestamp ? parseInt(timestamp, 10) : null;
};

export const isTokenExpired = (): boolean => {
  const timestamp = getAuthTokenTimestamp();
  if (!timestamp) return true; // No timestamp means expired/invalid
  const elapsed = Date.now() - timestamp;
  return elapsed >= TOKEN_TTL_MS;
};

export const getTokenRemainingTime = (): number => {
  const timestamp = getAuthTokenTimestamp();
  if (!timestamp) return 0;
  const elapsed = Date.now() - timestamp;
  const remaining = TOKEN_TTL_MS - elapsed;
  return remaining > 0 ? remaining : 0;
};

export const getRefreshToken = (): string | null => {
  return getCookie('refreshToken');
};

export const clearTokens = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authTokenTimestamp');
  deleteCookie('refreshToken');
};

// Refresh token logic
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

export const refreshAuthToken = async (): Promise<string | null> => {
  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeTokenRefresh((token: string) => {
        resolve(token);
      });
    });
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.error('No refresh token available');
    return null;
  }

  isRefreshing = true;

  try {
    // Step 1: Get new tokens
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/v1/refreshTokenV1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: refreshToken,
        }),
      });
    } catch (networkError) {
      console.error('Network error during token refresh:', networkError);
      isRefreshing = false;
      return null;
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Failed to parse refresh token response:', parseError);
      isRefreshing = false;
      return null;
    }

    if (!response.ok) {
      throw new Error(data.message || 'Failed to refresh token');
    }

    const newAuthToken = data.data?.token;
    const newRefreshToken = data.data?.refreshToken?.token;

    if (!newAuthToken || !newRefreshToken) {
      throw new Error('Invalid refresh token response');
    }

    // Save new tokens
    saveTokens(newAuthToken, newRefreshToken);

    // Step 2: Acknowledge token rotation (if required)
    if (data.data?.requiresAcknowledgment) {
      try {
        await fetch(`${API_BASE_URL}/auth/v1/refreshTokenV1`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: newRefreshToken,
            acknowledgeToken: true,
          }),
        });
      } catch (ackError) {
        // Acknowledgment failure is non-critical
        console.warn('Token acknowledgment failed:', ackError);
      }
    }

    isRefreshing = false;
    onTokenRefreshed(newAuthToken);
    return newAuthToken;
  } catch (error: any) {
    console.error('Token refresh failed:', error);
    isRefreshing = false;
    onTokenRefreshed('');

    // Clear tokens on refresh failure
    clearTokens();
    return null;
  }
};

// Enhanced fetch wrapper with automatic token refresh
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const authToken = getAuthToken();

  if (!authToken) {
    throw new Error('No authentication token available');
  }

  // Add auth headers
  const headers = {
    ...options.headers,
    Authorization: authToken,
  };

  // Make the request
  let response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized (401), try to refresh the token and retry
  if (response.status === 401) {
    const newToken = await refreshAuthToken();

    if (!newToken) {
      // Refresh failed, user needs to log in again
      throw new Error('Session expired. Please log in again.');
    }

    // Retry the request with new token
    headers.Authorization = newToken;
    response = await fetch(url, {
      ...options,
      headers,
    });
  }

  return response;
};

// Logout helper
export const logout = () => {
  clearTokens();
  // Redirect to login page
  window.location.href = '/';
};
